import { useSelector } from 'react-redux';
import { isEmpty } from '@belong/common';
import { STEP_GATE_NAMES } from 'accounts/constants/steps';
import { getFilteredGateSteps } from 'accounts/utils/flow-steps';
import { getPropertySection, getUserSection } from 'accounts/utils/sections';
import type { UnitLeaseDetailsModel, FlowStepModel } from 'api/models';
import HomeownerAccountProperty from 'models/homeownerAccounts/HomeownerAccountProperty';
import {
  selectUser,
  selectUserFlows,
  selectUserReports,
} from 'store/redux/user/selectors';
import { useHomeownerInspections } from './use-homeowner-inspections';
import { useSelectedProperty } from './use-selected-property';

type UseSetupFlowSectionsProps = {
  // Both are guarded below (`isEmpty(allStepsByGate) || !properties`) because
  // `useSteps` yields them as null until the flows have loaded.
  properties: HomeownerAccountProperty[] | null | undefined;
  // A record of gate name -> that gate's steps, which is what `useSteps` builds
  // and what `getFilteredGateSteps` keys over — not an array of records.
  allStepsByGate: Record<string, FlowStepModel[]> | null;
  selectedProperty?: HomeownerAccountProperty;
  units?: UnitLeaseDetailsModel;
};

/**
 * The two sections come from `getUserSection` / `getPropertySection`, which build
 * ad-hoc objects out of untyped step data, and the early return below hands back
 * `[]` for each. Declaring the return keeps that shape difference from collapsing
 * to a `never[] | {...}` union that callers cannot read `.isCompleted` off.
 */
type SetupFlowSections = {
  propertySection: any;
  accountSection: any;
  isFlowIncomplete: boolean;
  isPricingImprovementReady: boolean;
  shouldShowCommonSteps: boolean;
};

export function useSetupFlowSections({
  properties,
  allStepsByGate,
  selectedProperty: customSelectedProperty,
  units,
}: UseSetupFlowSectionsProps): SetupFlowSections {
  const user = useSelector(selectUser);
  const reports = useSelector(selectUserReports);
  const flows = useSelector(selectUserFlows);
  const { selectedProperty } = useSelectedProperty();
  const propertyToUse = customSelectedProperty || selectedProperty;
  const { inspections } = useHomeownerInspections();

  if (isEmpty(allStepsByGate) || !properties || !propertyToUse) {
    return {
      propertySection: [],
      accountSection: [],
      isFlowIncomplete: false,
      isPricingImprovementReady: false,
      shouldShowCommonSteps: true,
    };
  }

  const stepsByGate = getFilteredGateSteps({
    // `isEmpty(null)` is true, so the guard above already returned for a null map.
    allStepsByGate: allStepsByGate!,
    unitId: null,
    selectedProperty: propertyToUse,
    user,
  });

  const newAccountSection = getUserSection({
    stepsByGate,
    property: propertyToUse,
    flows,
  });

  const newPropertySection = getPropertySection({
    stepsByGate,
    selectedProperty: propertyToUse,
    reports,
    flows,
    inspections,
    units,
  });

  const isIncompleteFlow = newPropertySection.some(
    (newProperty: any) => !newProperty.isCompleted,
  );

  const isReadyPricingImprovement = newPropertySection.some((property: any) =>
    property.subSections.some(
      (subSection: any) =>
        [
          STEP_GATE_NAMES.HomeImprovements,
          STEP_GATE_NAMES.HomePricing,
        ].includes(subSection.name) && !subSection.disabled,
    ),
  );

  // common steps are part of build listing and inspection schedule gates
  const newShouldShowCommonSteps =
    !newAccountSection.isCompleted ||
    !newPropertySection.every((section: any) =>
      section.subSections
        .filter((subSection: any) =>
          [
            STEP_GATE_NAMES.InspectionSchedule,
            STEP_GATE_NAMES.BuildListing,
          ].includes(subSection.name),
        )
        .every((subSection: any) => subSection.isCompleted),
    );

  return {
    propertySection: newPropertySection,
    accountSection: newAccountSection,
    isFlowIncomplete: isIncompleteFlow,
    isPricingImprovementReady: isReadyPricingImprovement,
    shouldShowCommonSteps: newShouldShowCommonSteps,
  };
}
